import { useState, useMemo } from 'react';
import { useContextAwareGetFastActivitiesByProject } from '@hooks/index';

interface UseActivitiesPaginationParams {
  projectId: string;
  itemsPerPage: number;
}

interface UseActivitiesPaginationReturn {
  activities: any[];
  activitiesCount: number;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: any;
  currentPage: number;
  handlePageChange: (page: number) => void;
  handleNext: () => void;
  handlePrev: () => void;
  hasNext: boolean;
  hasPrev: boolean;
  refetch: () => void;
}

export const useActivitiesPagination = ({
  projectId,
  itemsPerPage,
}: UseActivitiesPaginationParams): UseActivitiesPaginationReturn => {
  const [currentPage, setCurrentPage] = useState(1);
  
  const isProjectIdValid = useMemo<boolean>(
    () => projectId !== undefined && projectId !== "new",
    [projectId]
  );

  const {
    data: activitiesRes,
    isLoading,
    isFetching,
    refetch,
    isError,
    error,
  } = useContextAwareGetFastActivitiesByProject({
    projectId: parseInt(projectId),
    page: currentPage,
    pageSize: itemsPerPage,
  }, { 
    skip: !isProjectIdValid,
    refetchOnMountOrArgChange: true 
  });

  const activities = activitiesRes?.results || [];
  const activitiesCount = activitiesRes?.count || 0;
  const hasNext = !!activitiesRes?.next;
  const hasPrev = !!activitiesRes?.previous;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleNext = () => {
    if (hasNext) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (hasPrev) {
      setCurrentPage(prev => prev - 1);
    }
  };

  return {
    activities,
    activitiesCount,
    isLoading,
    isFetching,
    isError,
    error,
    currentPage,
    handlePageChange,
    handleNext,
    handlePrev,
    hasNext,
    hasPrev,
    refetch,
  };
};
